import { SYSTEM_ADMIN_TENANT_UUID } from '../auth/AuthProvider';
import { User } from '../auth/User';
import { IShellPluginConfig } from '../shell/IShellPluginConfig';
import { ApiRoutes, getFullApiRoute, replaceRouteParams } from './ApiRoutes';
import { HttpRequest } from './HttpRequest';

/** System admin sentinel — frontend uses the literal string 'system-admin' as a company ID placeholder. */
const SYSTEM_ADMIN_ID = 'system-admin';

/** Check if a value is a valid UUID (used to guard API calls that require real tenant IDs). */
const isValidUuid = (value: unknown): boolean =>
  typeof value === 'string' &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

/** Resolve frontend sentinel 'system-admin' to the UUID the backend expects. */
const resolveCompanyId = (companyId: string): string =>
  companyId === SYSTEM_ADMIN_ID ? SYSTEM_ADMIN_TENANT_UUID : companyId;

/** Validate that a companyId is safe to pass to backend APIs. */
const canMakeTenantApiCall = (companyId: string): boolean => {
  if (!companyId) return false;
  // 'system-admin' is a frontend-only sentinel — never send it as-is to the backend.
  // Callers should resolve it to SYSTEM_ADMIN_TENANT_UUID via resolveCompanyId first.
  if (companyId === SYSTEM_ADMIN_ID) return false;
  // Require a valid UUID format — prevents malformed IDs from reaching the backend
  return isValidUuid(companyId);
};

/** Shape of plugin features for a tenant company. */
export interface TenantPluginFeature {
  id: string;
  tenantPluginId: string;
  featureId: string;
  isEnabled: boolean;
  featureDetails: {
    name: string;
    custom: Record<string, string | boolean | number>;
    id?: string;
  };
  tenantDetails: {
    tenantName: string;
    pluginName: string;
    tenantId: string;
    pluginId: string;
    shellRoute?: string;
  };
}

export class PluginAPI {
  //This will get all plugins = this is not restricted to tenantId
  static async getAll(
    user: User,
    sidebar: boolean = false,
    shouldHideSuperAdmin?: 'true' | 'false',
  ): Promise<IShellPluginConfig[]> {
    const route = sidebar ? ApiRoutes.PLUGINS_V3_SIDEBAR : ApiRoutes.PLUGINS_V3;
    const url = `${getFullApiRoute(route)}${shouldHideSuperAdmin ? `?shouldHideSuperAdmin=${shouldHideSuperAdmin}` : ''}`;
    const result = await HttpRequest(url, 'GET', null, null, user);
    return result?.data || [];
  }

  // Dedicated fetch for ApplicationContainer — uses its own backend route so failures
  // are isolated from the sidebar and other plugin consumers.
  static async getAllForApplication(
    user: User,
    shouldHideSuperAdmin?: 'true' | 'false',
  ): Promise<IShellPluginConfig[]> {
    const url = `${getFullApiRoute(ApiRoutes.PLUGINS_V3_APP_CONFIG)}${shouldHideSuperAdmin ? `?shouldHideSuperAdmin=${shouldHideSuperAdmin}` : ''}`;
    const result = await HttpRequest(url, 'GET', null, null, user);
    return result?.data || [];
  }

  // Filter plugins with optional query parameters
  static async filter(
    user: User,
    filters?: {
      category?: string;
      type?: string;
      status?: string;
      createdBy?: string;
      createdStartDate?: string;
      createdEndDate?: string;
    },
  ): Promise<IShellPluginConfig[]> {
    const queryParams = new URLSearchParams();
    if (filters?.category) {
      queryParams.append('category', filters.category);
    }
    if (filters?.type) {
      queryParams.append('type', filters.type);
    }
    if (filters?.status) {
      queryParams.append('status', filters.status);
    }
    if (filters?.createdBy) {
      queryParams.append('createdBy', filters.createdBy);
    }
    if (filters?.createdStartDate) {
      queryParams.append('createdStartDate', filters.createdStartDate);
    }
    if (filters?.createdEndDate) {
      queryParams.append('createdEndDate', filters.createdEndDate);
    }
    const url = `${getFullApiRoute(ApiRoutes.PLUGINS_V3)}/filter${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const result = await HttpRequest(url, 'GET', null, null, user);
    return result?.data || [];
  }

  // get single plugin
  static async getOne(user: User, id: string): Promise<IShellPluginConfig | undefined> {
    const result = await HttpRequest(
      getFullApiRoute(ApiRoutes.PLUGINS_V3) + `/${id}`,
      'GET',
      null,
      null,
      user,
    );
    return result && result.data
      ? Array.isArray(result.data)
        ? result.data[0]
        : result.data
      : undefined;
  }

  static async updateOne(
    user: User,
    data: IShellPluginConfig,
  ): Promise<IShellPluginConfig | undefined> {
    const result = await HttpRequest(
      getFullApiRoute(data.id ? ApiRoutes.PLUGINS_V3 : ApiRoutes.PLUGINS_V3) +
        `${data.id ? `/${data.id}` : ''}`,
      data.id ? 'PUT' : 'POST',
      data,
      new Headers({ 'Content-Type': 'application/json' }),
      user,
    );
    //Check if we have a result and if it is object or array, if array just retun the first item as that is what we expect
    return result && result.data
      ? Array.isArray(result.data)
        ? result.data[0]
        : result.data
      : undefined;
  }

  static async deleteOne(user: User, data: IShellPluginConfig): Promise<boolean | undefined> {
    const result = await HttpRequest(
      getFullApiRoute(ApiRoutes.PLUGINS_V3) + `/${data.id}`,
      'DELETE',
      null,
      null,
      user,
    );
    //Check if we have a result and if it is object or array, if array just retun the first item as that is what we expect
    return result && result.data
      ? Array.isArray(result.data)
        ? result.data[0]
        : result.data
      : undefined;
  }

  static async bulkDelete(user: User, data: IShellPluginConfig[]) {
    const payload = {
      ids: data.map((plugin) => plugin.id).filter((id): id is string => !!id),
    };

    const result = await HttpRequest(
      getFullApiRoute(ApiRoutes.PLUGINS_V3),
      'DELETE',
      payload,
      new Headers({ 'Content-Type': 'application/json' }),
      user,
    );

    return result && result.data
      ? Array.isArray(result.data)
        ? result.data[0]
        : result.data
      : undefined;
  }

  // Get plugin features by pluginId
  static async getPluginFeatures(
    user: User,
    pluginId: string,
  ): Promise<
    Array<{
      id: string;
      pluginId: string;
      name: string;
      permission: string[];
      isAdvancedFeature: boolean;
      description: string;
      inputType: string;
      custom: Record<string, unknown>;
    }>
  > {
    const url = `${getFullApiRoute(ApiRoutes.PLUGIN_FEATURES_V3)}?pluginId=${pluginId}`;
    const result = await HttpRequest(url, 'GET', null, null, user);

    // API returns { data: [[{...features...}]] }
    if (result && result?.data?.length > 0) {
      const featuresArray = result?.data[0];
      if (Array.isArray(featuresArray)) {
        return featuresArray;
      }
    }
    return [];
  }

  // Get tenant plugin features by tenantPluginIds (batch)
  static async getTenantPluginFeatures(
    user: User,
    tenantPluginIds: string[],
  ): Promise<
    Array<{
      id: string;
      tenantPluginId: string;
      featureId: string;
      isEnabled: boolean;
      createdAt?: string;
      updatedAt?: string;
    }>
  > {
    if (!tenantPluginIds.length) return [];

    const url = getFullApiRoute(ApiRoutes.TENANT_PLUGIN_FEATURES_BATCH_V3);
    const result = await HttpRequest(
      url,
      'POST',
      { tenantPluginId: tenantPluginIds },
      new Headers({ 'Content-Type': 'application/json' }),
      user,
    );

    if (result) {
      // The API returns data as an array of arrays, so we need to flatten it
      const data = result?.data;
      if (Array.isArray(data)) {
        // Flatten the nested array structure
        return data.flat();
      }
      return [];
    }
    return [];
  }

  // Create/Update tenant plugin features
  static async updateTenantPluginFeatures(
    user: User,
    features: Array<{
      id?: string;
      tenantPluginId: string;
      featureId: string;
      isEnabled: boolean;
    }>,
  ): Promise<unknown> {
    const url = getFullApiRoute(ApiRoutes.TENANT_PLUGIN_FEATURES_V3);
    const result = await HttpRequest(
      url,
      'POST',
      { tenantPluginsFeatures: features },
      new Headers({ 'Content-Type': 'application/json' }),
      user,
    );
    return result?.data;
  }

  // Get plugin features for a company/tenant by companyId
  static async getCompanyPluginFeatures(
    user: User,
    companyId: string,
  ): Promise<TenantPluginFeature[]> {
    const resolvedId = resolveCompanyId(companyId);
    if (!canMakeTenantApiCall(resolvedId)) return [];

    const url = replaceRouteParams(
      getFullApiRoute(ApiRoutes.TENANT_PLUGIN_FEATURES_BY_COMPANY_V3),
      {
        companyId: resolvedId,
      },
    );
    const result = await HttpRequest(url, 'GET', null, null, user);
    return result?.data || [];
  }

  // Get tenant plugin features with per-action permission grants (V3)
  static async getCompanyPluginFeaturesWithPermissions(
    user: User,
    companyId: string,
  ): Promise<
    Array<{
      id: string;
      name: string;
      description?: string;
      permissions: string[];
      shellRoute?: string;
    }>
  > {
    const resolvedId = resolveCompanyId(companyId);
    if (!canMakeTenantApiCall(resolvedId)) return [];

    const url = replaceRouteParams(
      getFullApiRoute(ApiRoutes.TENANT_PLUGIN_FEATURES_WITH_PERMISSIONS),
      {
        companyId: resolvedId,
      },
    );
    const result = await HttpRequest(url, 'GET', null, null, user);
    return result?.data?.[0]?.features || [];
  }
}
